import { EventEmitter, Injectable, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { Logger } from '../logger.service';
import { Action } from '../models/action.model';

const log = new Logger('ErrorService');

// Markers that only show up in raw .NET/EF/SQL exception text (e.g. a
// DbUpdateException wrapping a FOREIGN KEY violation), never in a message
// actually written for a customer to read.
const TECHNICAL_ERROR_PATTERN =
  /inner exception|stack ?trace|sqlexception|foreign key constraint|entity changes|at System\.|exception of type|unhandled exception/i;

export type ErrorInput = string | Error | Record<string, unknown>;

interface ErrorHandlerPayload {
  action: Action | null;
  error: ErrorInput;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorService implements OnInit {
  onAnalyticsLog = new EventEmitter<unknown>();
  errorMsg = '';
  dir: string;

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.dir = localStorage.getItem('html-dir');
  }

  showErrorDialog(msg: ErrorInput, action: Action = null) {
    const data: ErrorHandlerPayload = {
      action: action,
      error: msg,
    };
    let errorRes = this.errorHandler(data);
    if (errorRes) {
      errorRes = this.toFriendlyMessage(errorRes);
    }
    if (errorRes) {
      Swal.fire({
        title: this.translateService.instant('Error'),
        html: this.translateService.instant(errorRes),
        icon: 'error',
        imageUrl: null,
        confirmButtonColor: '#242f5a',
        confirmButtonText: data.action
          ? this.translateService.instant(data.action.name)
          : this.translateService.instant('Ok'),
        showCancelButton: false,
        cancelButtonText: this.translateService.instant('Cancel'),
        reverseButtons: true,
        width: 700,
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.action(data);
      });
    }
  }

  errorHandler(data: ErrorHandlerPayload): string | undefined {
    const msg = data.error;
    if (msg) {
      if (msg instanceof Object) {
        const errorBody = (msg as Record<string, unknown>).error;
        const nestedError = (errorBody as Record<string, unknown> | undefined)
          ?.error;
        if (errorBody && nestedError) {
          this.errorMsg = String(nestedError);
        } else {
          this.errorMsg = JSON.stringify(errorBody);
        }
        return this.errorMsg;
      } else {
        this.errorMsg = String(msg);
        return this.errorMsg;
      }
    } else {
      this.errorMsg = JSON.stringify(data);
      return this.errorMsg;
    }
  }

  /** Swaps a raw backend/.NET exception message for a generic one a customer can actually act on. */
  private toFriendlyMessage(msg: string): string {
    if (typeof msg !== 'string' || !TECHNICAL_ERROR_PATTERN.test(msg)) {
      return msg;
    }
    log.error('Suppressed technical error from user-facing dialog', msg);
    return 'Something went wrong. Please try again.';
  }

  action(data: ErrorHandlerPayload) {
    if (data && data.action && data.action.emit) {
      data.action.emit();
    }
  }
}
